import { cn } from "@omni/utils/styles";
import Tick from "@pqina/flip";
import { useEffect, useRef, useState } from "react";
import { ReturnValue, useCountUp } from "use-count-up";

import { usePreviousAttributeValue } from "@/utils/attributes";
import { CounterTheme } from "@/utils/enums";

import Config, { TextSize } from "./boxCounter.config";

type FlipProps = {
    value: string;
    textSize: TextSize;
    length: number;
    theme: string;
};

type BoxCounterProps = {
    length: number;
    currentValue: number;
    textSize: TextSize;
    theme: CounterTheme;
};

const Flip = ({ value, textSize, length, theme }: FlipProps) => {
    const divRef = useRef<HTMLDivElement>(null);
    const tickRef = useRef<any>(null);

    const [fontSize, setFontSize] = useState<string>();

    useEffect(() => {
        if (!divRef.current) return;

        Tick.DOM.create(divRef.current, {
            value,
            repeat: true,
            didInit: (tick: any) => {
                tickRef.current = tick;
            },
            view: {
                children: [
                    {
                        root: "div",
                        style: ".tick",
                        repeat: true,
                        children: [
                            {
                                view: "flip"
                            }
                        ]
                    }
                ]
            }
        });

        return () => {
            if (tickRef.current) {
                Tick.DOM.destroy(tickRef.current);
            }
        };
    }, [value]);

    useEffect(() => {
        if (tickRef.current) {
            tickRef.current.value = value;
        }
    }, [value]);

    useEffect(() => {
        const element = divRef.current;
        if (!element) return;

        const parent = element.closest<HTMLDivElement>(
            ".box-banner--container"
        );

        if (!parent) return;

        const updateSize = () => {
            const { textWidth } = Config[textSize] ?? Config[TextSize.MEDIUM];

            setFontSize(
                `calc(${parent.offsetWidth}px * ${textWidth} / ${length})`
            );
        };

        updateSize();

        const observer = new ResizeObserver(updateSize);
        observer.observe(parent);

        return () => observer.disconnect();
    }, [textSize, length]);

    const sizeClasses = {
        sm: "tw:text-[clamp(1.5rem,1.293rem+0.516vw,2.25rem)]",
        md: "tw:text-[clamp(1.75rem,1.35875rem+1.305vw,3.27rem)]",
        lg: "tw:text-[clamp(2rem,1.5rem+3vw,5rem)]",
        xl: "tw:text-[clamp(2.5rem,1.875rem+7.5vw,8rem)]"
    };

    return (
        <div
            ref={divRef}
            style={fontSize ? { fontSize } : undefined}
            className={cn("box-counter", "tw:[&_.tick-credits]:hidden", {
                [sizeClasses[textSize]]: !fontSize,
                "tw:[&_.tick-flip-panel]:text-black tw:[&_.tick-flip-panel]:bg-grey-light tw:[&_.tick-flip-panel-back-shadow]:bg-[linear-gradient(180deg,rgba(0,0,0,0.1),rgba(0,0,0,0.1))] tw:[&_.tick-flip-panel-front-shadow]:bg-[linear-gradient(0deg,rgba(0,0,0,0.4),rgba(0,0,0,0.1))]":
                    theme === CounterTheme.LIGHT
            })}
        />
    );
};

const BoxCounter = ({
    length,
    currentValue,
    textSize,
    theme
}: BoxCounterProps) => {
    const prevAmount = usePreviousAttributeValue({ currentValue });
    const [start, setStart] = useState(currentValue);
    const [end, setEnd] = useState(currentValue);

    const { value, reset } = useCountUp({
        isCounting: true,
        start: start,
        end: end
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        setEnd(currentValue);
        setStart(prevAmount?.currentValue);
        reset();
    }, [currentValue]);

    const addLeadingZeros = (num: ReturnValue) =>
        String(num).padStart(length, "0");

    return (
        <Flip
            value={addLeadingZeros(value)}
            textSize={textSize}
            length={length}
            theme={theme}
        />
    );
};

export default BoxCounter;
